import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  doc, 
  setDoc, 
  updateDoc, 
  getDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { storage, auth, myelinersCollectionRef } from '../../firebase/config';

// Async thunk to add a new employee
export const addEmployee = createAsyncThunk(
  'employeeManagement/addEmployee',
  async ({ employeeData, profileImage }, { rejectWithValue }) => {
    try {
      // Check if email already exists
      const emailQuery = query(myelinersCollectionRef, where('email', '==', employeeData.email));
      const emailSnapshot = await getDocs(emailQuery);
      
      if (!emailSnapshot.empty) {
        return rejectWithValue('Email already exists in the system');
      }
      
      // Check if empID already exists
      const empIDQuery = query(myelinersCollectionRef, where('empID', '==', employeeData.empID));
      const empIDSnapshot = await getDocs(empIDQuery);
      
      if (!empIDSnapshot.empty) {
        return rejectWithValue('Employee ID already exists in the system');
      }

      // Create auth account if password is provided
      if (employeeData.password) {
        try {
          await createUserWithEmailAndPassword(auth, employeeData.email, employeeData.password);
        } catch (authError) {
          console.error('Error creating auth account:', authError);
          return rejectWithValue(`Authentication error: ${authError.message}`);
        }
      }
      
      // Upload profile image if provided
      let imageUrl = '';
      if (profileImage) {
        const imageRef = ref(storage, `profile-pictures/${employeeData.email}_${Date.now()}`);
        await uploadBytes(imageRef, profileImage);
        imageUrl = await getDownloadURL(imageRef);
      }
      
      // Prepare employee data
      const newEmployeeData = {
        ...employeeData,
        img: imageUrl || '',
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        userType: employeeData.userType || 'user',
        leaveBalance: parseInt(employeeData.leaveEntitlement || 21, 10),
        leaveEntitlement: parseInt(employeeData.leaveEntitlement || 21, 10),
        leaveTaken: 0,
        myelinLive: true
      };
      
      // Remove password from Firestore data
      delete newEmployeeData.password;
      
      // Add to Firestore using email as document ID
      const newEmployeeRef = doc(myelinersCollectionRef, employeeData.email);
      await setDoc(newEmployeeRef, newEmployeeData);
      
      return {
        id: employeeData.email,
        ...newEmployeeData
      };
    } catch (error) {
      console.error('Error adding employee:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to update employee information
export const updateEmployee = createAsyncThunk(
  'employeeManagement/updateEmployee',
  async ({ empID, employeeData, profileImage }, { rejectWithValue }) => {
    try {
      const employeeRef = doc(myelinersCollectionRef, empID);
      const employeeDoc = await getDoc(employeeRef);
      
      if (!employeeDoc.exists()) {
        return rejectWithValue('Employee not found');
      }
      
      // Upload profile image if provided
      let imageUrl = employeeData.img || '';
      if (profileImage) {
        const imageRef = ref(storage, `profile-pictures/${employeeData.email}_${Date.now()}`);
        await uploadBytes(imageRef, profileImage);
        imageUrl = await getDownloadURL(imageRef);
      }
      
      // Prepare updated data
      const updatedData = {
        ...employeeData,
        img: imageUrl,
        lastUpdated: serverTimestamp()
      };
      
      // Update Firestore
      await updateDoc(employeeRef, updatedData);
      
      return {
        id: empID,
        ...updatedData
      };
    } catch (error) {
      console.error('Error updating employee:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch an employee's details by email
export const fetchEmployeeByEmail = createAsyncThunk(
  'employeeManagement/fetchEmployeeByEmail',
  async (email, { rejectWithValue }) => {
    try {
      const employeeRef = doc(myelinersCollectionRef, email);
      const employeeDoc = await getDoc(employeeRef);
      
      if (!employeeDoc.exists()) {
        return rejectWithValue('Employee not found');
      }
      
      return {
        id: email,
        ...employeeDoc.data()
      };
    } catch (error) {
      console.error('Error fetching employee:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Legacy function for backward compatibility - now fetches by email
export const fetchEmployeeById = createAsyncThunk(
  'employeeManagement/fetchEmployeeById',
  async (identifier, { rejectWithValue }) => {
    try {
      // If identifier contains @, treat it as email, otherwise treat as empID and query
      if (identifier.includes('@')) {
        const employeeRef = doc(myelinersCollectionRef, identifier);
        const employeeDoc = await getDoc(employeeRef);
        
        if (!employeeDoc.exists()) {
          return rejectWithValue('Employee not found');
        }
        
        return {
          id: identifier,
          ...employeeDoc.data()
        };
      } else {
        // Query by empID for backward compatibility
        const empIDQuery = query(myelinersCollectionRef, where('empID', '==', identifier));
        const empIDSnapshot = await getDocs(empIDQuery);
        
        if (empIDSnapshot.empty) {
          return rejectWithValue('Employee not found');
        }
        
        const employeeDoc = empIDSnapshot.docs[0];
        return {
          id: employeeDoc.id,
          ...employeeDoc.data()
        };
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch all employees
export const fetchAllEmployees = createAsyncThunk(
  'employeeManagement/fetchAllEmployees',
  async (_, { rejectWithValue }) => {
    try {
      const snapshot = await getDocs(myelinersCollectionRef);
      
      const employees = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return employees;
    } catch (error) {
      console.error('Error fetching employees:', error);
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  loading: false,
  error: null,
  success: false,
  currentEmployee: null,
  employees: []
};

// Create the slice
const employeeManagementSlice = createSlice({
  name: 'employeeManagement',
  initialState,
  reducers: {
    resetEmployeeState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.currentEmployee = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Add employee reducers
    builder
      .addCase(addEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addEmployee.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(addEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to add employee';
      });
    
    // Update employee reducers
    builder
      .addCase(updateEmployee.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateEmployee.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateEmployee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update employee';
      });
    
    // Fetch employee reducers
    builder
      .addCase(fetchEmployeeById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEmployee = action.payload;
      })
      .addCase(fetchEmployeeById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch employee';
      });
    
    // Fetch all employees reducers
    builder
      .addCase(fetchAllEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllEmployees.fulfilled, (state, action) => {
        state.loading = false;
        state.employees = action.payload;
      })
      .addCase(fetchAllEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch employees';
      });
  }
});

export const { resetEmployeeState, clearError } = employeeManagementSlice.actions;
export default employeeManagementSlice.reducer; 